import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import ProjectDetails from "./pages/ProjectDetails";
import Footer from "./components/Footer";
import { useState, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import Projects from "./components/Projects";

import Error from "./components/Error";
// import IconButton from "@mui/material/IconButton";
// import Brightness4Icon from "@mui/icons-material/Brightness4";
// import Brightness7Icon from "@mui/icons-material/Brightness7";

const App = () => {
  const [mode, setMode] = useState("dark"); // Default to dark for tech vibe

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
                primary: { main: "#2563eb" }, // Tech blue
                secondary: { main: "#14b8a6" }, // Teal
                background: { default: "#f8fafc", paper: "#ffffff" },
                code: { background: "#f1f5f9", text: "#334155" },
              }
            : {
                primary: { main: "#38bdf8" }, // Bright tech blue
                secondary: { main: "#2dd4bf" }, // Bright teal
                background: { default: "#0f172a", paper: "#1e293b" }, // Dark tech colors
                text: { primary: "#e2e8f0", secondary: "#94a3b8" },
                code: { background: "#1e293b", text: "#e2e8f0" },
              }),
        },
        typography: {
          fontFamily: '"IBM Plex Mono", "Roboto Mono", monospace',
          h1: {
            fontWeight: 700,
            fontSize: "2.5rem",
            background:
              mode === "dark"
                ? "linear-gradient(90deg, #38bdf8, #2dd4bf)"
                : "linear-gradient(90deg, #2563eb, #14b8a6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            "@media (min-width:600px)": { fontSize: "4rem" },
          },
          h2: {
            fontWeight: 600,
            fontSize: "1.8rem",
            borderBottom:
              mode === "dark" ? "2px solid #38bdf8" : "2px solid #2563eb",
            display: "inline-block",
            paddingBottom: "0.3rem",
          },
          body1: {
            lineHeight: 1.8,
            fontSize: "1.1rem",
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                padding: "8px 16px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow:
                    mode === "dark"
                      ? "0 10px 25px rgba(56, 189, 248, 0.2)"
                      : "0 10px 25px rgba(37, 99, 235, 0.1)",
                },
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor:
                  mode === "dark"
                    ? "rgba(56, 189, 248, 0.3)"
                    : "rgba(37, 99, 235, 0.3)",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Navbar colorMode={colorMode} mode={mode} />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="*" element={<Error />} />
            <Route path="/" element={<Home />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetails />} />
          </Routes>
        </AnimatePresence>
        <Footer />
      </Router>
    </ThemeProvider>
  );
};

export default App;
